import { motion } from 'framer-motion';
import { FaWhatsapp, FaArrowRight } from 'react-icons/fa';

const floatingItems = [
  { emoji: '📚', x: '8%', y: '15%', size: 'text-5xl', delay: 0, duration: 3.5 },
  { emoji: '🦕', x: '88%', y: '20%', size: 'text-6xl', delay: 0.5, duration: 4 },
  { emoji: '🌈', x: '5%', y: '65%', size: 'text-4xl', delay: 1, duration: 3 },
  { emoji: '⭐', x: '92%', y: '70%', size: 'text-5xl', delay: 0.3, duration: 4.5 },
  { emoji: '🎨', x: '15%', y: '80%', size: 'text-4xl', delay: 0.8, duration: 3.8 },
  { emoji: '🧩', x: '82%', y: '55%', size: 'text-4xl', delay: 0.6, duration: 3.2 },
  { emoji: '✏️', x: '78%', y: '82%', size: 'text-3xl', delay: 1.2, duration: 4.2 },
  { emoji: '💡', x: '25%', y: '12%', size: 'text-3xl', delay: 0.4, duration: 3.6 },
  { emoji: '🎯', x: '60%', y: '88%', size: 'text-3xl', delay: 0.9, duration: 3.3 },
];

const stats = [
  { value: '2', label: 'Editions', icon: '📖', color: '#7B2FBE' },
  { value: '35', label: 'Issues/Year', icon: '📅', color: '#1565C0' },
  { value: '30K+', label: 'Happy Readers', icon: '😊', color: '#3CB044' },
];

const Hero = () => {
  return (
    <section
      className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-cover bg-top bg-no-repeat"
      style={{
        backgroundImage: `url('/hero-bg.png')`,
      }}
    >
      {/* Overlay to improve text contrast and visual clarity */}
      <div className="absolute inset-0 bg-white/60 backdrop-blur-[4px] pointer-events-none z-0" />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(60,176,68,0.12) 1.5px, transparent 1.5px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Large decorative blobs */}
      <div
        className="absolute top-0 left-0 w-[40vw] h-[40vw] opacity-25 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(123,47,190,0.25) 0%, transparent 70%)',
          filter: 'blur(60px)',
          transform: 'translate(-20%, -20%)',
        }}
      />
      <div
        className="absolute bottom-0 right-0 w-[50vw] h-[50vw] opacity-20 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(21,101,192,0.25) 0%, transparent 70%)',
          filter: 'blur(80px)',
          transform: 'translate(20%, 20%)',
        }}
      />
      <div
        className="absolute top-1/2 left-1/2 w-[60vw] h-[60vw] opacity-10 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(60,176,68,0.3) 0%, transparent 70%)',
          filter: 'blur(60px)',
          transform: 'translate(-50%, -50%)',
        }}
      />

      {/* Floating emoji elements */}
      {floatingItems.map((item, i) => (
        <motion.div
          key={i}
          className={`absolute ${item.size} select-none pointer-events-none`}
          style={{ left: item.x, top: item.y }}
          animate={{ y: [0, -22, 0], rotate: [0, 8, -8, 0] }}
          transition={{ duration: item.duration, repeat: Infinity, delay: item.delay, ease: 'easeInOut' }}
        >
          {item.emoji}
        </motion.div>
      ))}

      {/* Main Content */}
      <div className="container mx-auto px-3 sm:px-4 py-16 md:py-24 relative z-10">
        <div className="max-w-5xl mx-auto text-center p-5 sm:p-8 md:p-12 rounded-[2rem] md:rounded-[3.5rem] w-full"
             style={{
               background: 'rgba(255, 255, 255, 0.4)',
               backdropFilter: 'blur(16px)',
               border: '1px solid rgba(255, 255, 255, 0.7)',
               boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
             }}
        >

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-display font-semibold mb-8"
            style={{
              background: 'linear-gradient(135deg, rgba(60,176,68,0.12), rgba(21,101,192,0.12))',
              border: '1px solid rgba(60,176,68,0.3)',
              color: '#1E7C24',
              backdropFilter: 'blur(8px)',
            }}
          >
            <span>✨</span>
            <span>India's Favourite Children's Weekly Magazine</span>
            <span>✨</span>
          </motion.div>

          {/* Logo — wide banner style */}
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.9, type: 'spring', stiffness: 160, damping: 18 }}
            className="mb-10 flex justify-center"
          >
            <div className="relative inline-block">
              {/* Glow ring */}
              <motion.div
                className="absolute inset-0 rounded-3xl -z-10"
                style={{
                  background: 'radial-gradient(ellipse, rgba(60,176,68,0.25) 0%, transparent 70%)',
                  filter: 'blur(40px)',
                  transform: 'scale(1.4)',
                }}
                animate={{ scale: [1.3, 1.6, 1.3], opacity: [0.5, 0.8, 0.5] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <motion.img
                src="/logo.png"
                alt="The Tiny Times - Small Steps Into A Big World"
                className="h-auto w-auto object-contain drop-shadow-2xl"
                style={{ maxWidth: 'min(480px, 85vw)', maxHeight: '180px' }}
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </motion.div>

          {/* Tagline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="text-xl md:text-2xl lg:text-3xl font-display font-semibold mb-4"
            style={{ color: '#2A5F8F' }}
          >
            Weekly fun stories, puzzles & joyful learning
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="text-base md:text-lg text-neutral-gray-dark mb-12 max-w-2xl mx-auto leading-relaxed"
          >
            A delightful children's magazine packed with adventures, brain teasers, creative corners and cultural stories.
            Perfectly crafted for ages <strong className="text-primary-green-dark">4–15</strong> 🌟
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-10 md:mb-16 w-full px-2"
          >
            <motion.a
              href="https://wa.me/919618377750"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.97 }}
              className="btn-whatsapp text-lg md:text-xl px-6 md:px-10 py-3 md:py-4 w-full sm:w-auto flex flex-row items-center justify-center gap-2"
            >
              <FaWhatsapp className="text-xl md:text-2xl flex-shrink-0" />
              <span className="whitespace-nowrap">Subscribe via WhatsApp 🚀</span>
            </motion.a>

            <motion.a
              href="/samples"
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.97 }}
              className="btn-secondary text-lg md:text-xl px-6 md:px-10 py-3 md:py-4 w-full sm:w-auto flex flex-row items-center justify-center gap-2"
            >
              <span className="whitespace-nowrap">Read Free Samples</span>
              <FaArrowRight className="text-sm md:text-base flex-shrink-0" />
            </motion.a>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="grid grid-cols-3 gap-2 sm:gap-4 max-w-2xl mx-auto w-full px-1"
          >
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -4, scale: 1.04 }}
                className="text-center rounded-xl md:rounded-2xl p-2 sm:p-4 md:p-5"
                style={{
                  background: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.8)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
                }}
              >
                <div className="text-3xl mb-1">{stat.icon}</div>
                <div
                  className="text-2xl sm:text-3xl md:text-4xl font-display font-bold mb-1 tracking-tight"
                  style={{ color: stat.color }}
                >
                  {stat.value}
                </div>
                <div className="text-xs md:text-sm font-display font-semibold text-neutral-gray-dark">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>

        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        <svg viewBox="0 0 1440 100" className="w-full" preserveAspectRatio="none" style={{ display: 'block' }}>
          <path
            fill="white"
            d="M0,50L60,55C120,60,240,70,360,65C480,60,600,40,720,38C840,35,960,45,1080,50C1200,55,1320,50,1380,48L1440,45L1440,100L0,100Z"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;